// Board card chrome — Screen 3 card + ambiguous-transition Popper (states 1–5).
import { useRef } from "react";
import { Box, ClickAwayListener, Popper } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import { Icon, PersonAvatar } from "../../../mui";
import type { JiraTransitionOption } from "../../../lib/brain-api";
import type { BoardIssue } from "./useBoardDnd";

const c = brainTokens.color;

export function BoardCard({
  issue,
  quiet = false,
  sunk = false,
  spine,
  lifting = false,
  settling = false,
  ambiguousOptions = null,
  onPickTransition,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  issue: BoardIssue;
  quiet?: boolean;
  sunk?: boolean;
  /** 3px left status spine color (planning/backlog/complete) — omit for none. */
  spine?: string;
  lifting?: boolean;
  settling?: boolean;
  ambiguousOptions?: JiraTransitionOption[] | null;
  onPickTransition?: (t: JiraTransitionOption) => void;
  onOpen?: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Suppress the synthetic click that follows dragend so DnD never opens the drawer. */
  const draggedRef = useRef(false);
  const statusWord = issue.displayStatus ?? issue.status;
  const moving = statusWord === "Moving…";

  const borderColor = settling
    ? c.tealTintBorder
    : quiet
      ? "transparent"
      : issue.flagged
        ? c.dangerBorder
        : c.border;
  const bg = settling ? c.tealTint2 : quiet ? c.panelBg3 : c.pageBg;

  return (
    <Box
      ref={rootRef}
      draggable
      tabIndex={0}
      role="button"
      aria-label={`Open ${issue.key}: ${issue.summary}`}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();onOpen?.();
        }
      }}
      onClick={() => {
        if (draggedRef.current) {
          draggedRef.current = false;
          return;
        }
        onOpen?.();
      }}
      onDragStart={(e) => {
        draggedRef.current = true;
        e.dataTransfer.setData("text/plain", issue.key);
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={() => {
        onDragEnd();
        // Clear after the browser's post-drag click (if any) so the next real click still opens.
        queueMicrotask(() => {
          setTimeout(() => {
            draggedRef.current = false;
          }, 0);
        });
      }}
      sx={{
        position: "relative",
        p: quiet ? "10px 12px" : "11px 12px",
        pl: spine ? "13px" : undefined,
        border: `1px solid ${borderColor}`,
        borderLeft: spine ? `3px solid ${spine}` : undefined,
        borderRadius: `${brainTokens.radius.card}px`,
        background: bg,
        cursor: "grab",
        opacity: lifting ? 0.4 : sunk ? 0.55 : 1,
        transition: "border-color 140ms ease, background 140ms ease, opacity 140ms ease",
        "&:hover": {
          borderColor: quiet ? c.border : c.hoverBorder,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 11.5,
            color: quiet || sunk ? c.muted2 : c.tealInk,
            flex: "none",
            whiteSpace: "nowrap",
          }}
        >
          {issue.key}
        </Box>
        {issue.flagged && !quiet && (
          <Box
            sx={{
              fontSize: 10.5,
              color: c.dangerText,
              background: c.dangerBg,
              borderRadius: `${brainTokens.radius.pill}px`,
              px: "7px",
              py: "1px",
            }}
          >
            blocked
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {!quiet && (
          <Box
            component="a"
            href={issue.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Jira"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ display: "flex", color: c.faint, "&:hover": { color: c.tealInk } }}
          >
            <Icon name="arrow-right" size={14} />
          </Box>
        )}
        {quiet && (
          <Box sx={{ fontSize: 11, color: c.successText }}>{statusWord}</Box>
        )}
      </Box>

      <Box
        sx={{
          mt: "5px",
          fontSize: quiet ? 12.5 : 13,
          lineHeight: 1.45,
          color: quiet || sunk ? c.muted : c.ink2,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {issue.summary}
      </Box>

      {!quiet && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px" }}>
          <PersonAvatar name={issue.assignee ?? "Unassigned"} size={20} />
          <Box sx={{ fontSize: 11.5, color: c.muted }}>{issue.assignee ?? "Unassigned"}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: 11, color: moving ? c.tealInk : c.muted2 }}>{statusWord}</Box>
        </Box>
      )}

      {ambiguousOptions && ambiguousOptions.length > 0 && (
        <AmbiguousPopper
          anchorEl={rootRef.current}
          options={ambiguousOptions}
          onPick={(t) => onPickTransition?.(t)}
        />
      )}
    </Box>
  );
}

function AmbiguousPopper({
  anchorEl,
  options,
  onPick,
}: {
  anchorEl: HTMLElement | null;
  options: JiraTransitionOption[];
  onPick: (t: JiraTransitionOption) => void;
}) {
  const prompt =
    options.length === 2
      ? "Two transitions land here. Which one?"
      : `${options.length} transitions land here. Which one?`;

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
      sx={{ zIndex: 5 }}
    >
      <ClickAwayListener onClickAway={() => { /* stay on Moving… — never silent-guess */ }}>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            minWidth: 180,
            background: c.pageBg,
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.card}px`,
            boxShadow: brainTokens.shadow.heroCard,
            p: "10px",
          }}
        >
          <Box sx={{ fontSize: 12, color: c.muted, mb: "8px" }}>{prompt}</Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {options.map((t) => (
              <Box
                key={t.id}
                component="button"
                type="button"
                onClick={() => onPick(t)}
                sx={{
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontFamily: "inherit",
                  border: "none",
                  p: "6px 9px",
                  borderRadius: `${brainTokens.radius.chip}px`,
                  background: c.panelBg2,
                  color: c.ink2,
                  "&:hover": { background: c.tealTint2, color: c.tealInk },
                }}
              >
                → {t.toStatus}
              </Box>
            ))}
          </Box>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
}
